/**
 * Вкладка «Файлы для печати».
 *
 * Берёт реальный список с GET /designs/:code/production-files, рисует
 * миниатюры и даёт скачать оригинал; отдельно показывает шаги, которые упали,
 * — раньше про них не знал никто.
 *
 * Строка — ТОВАР, а не файл на диске: в печать по товару уходит один файл.
 * Маршрут PP2 создаёт на тот же товар две ветки листа (A4 и плоттер), в
 * хранилище из них попадает только плоттерная — поэтому ветки показаны внутри
 * строки, а «Хранилище» стоит у строки, а не у каждого файла.
 *
 * Усиление 11.10.2026: сводка «готово N из M товаров» с явными пропусками,
 * группы по носителю (категории), плитки или список с запоминанием выбора,
 * копирование артикула и пути хранилища, «Скачать все» по одному.
 */

import React from 'react'
import {
  AlertTriangle,
  Download,
  FileText,
  FileX,
  Info,
  LayoutGrid,
  List,
  RefreshCw,
} from 'lucide-react'
import { Badge } from '../../../../components/ui/Badge'
import { Button } from '../../../../components/ui/Button'
import { CopyButton } from '../../../../components/ui/CopyButton'
import { EmptyState } from '../../../../components/ui/EmptyState'
import { Skeleton } from '../../../../components/ui/Skeleton'
import { cn } from '../../../../lib/cn'
import { formatDateTime } from '../../lib'
import { downloadAuthorizedMedia } from '../../../../shared/media/authorizedMedia'
import type { DesignProductionFiles, ProductionFile, ProductionFileVariant } from '../../types'
import { parsePipelineError } from './pipelineError'
import { SafeImg } from './parts'

/** Откуда взять префикс и название носителя товара, у которого файла ещё нет. */
export interface PrintArticleMeta {
  prefix: string | null
  categoryName: string | null
}

interface PrintFilesTabProps {
  data: DesignProductionFiles | null
  /** Макет ещё не прошёл приёмку — файлы и не должны существовать */
  beforeAcceptance: boolean
  onPreview: (file: ProductionFile, variant: ProductionFileVariant) => void
  /** Товары макета: от них считается «готово N из M». null — ещё не загружены. */
  products?: Array<{ article: string }> | null
  articleMeta?: Record<string, PrintArticleMeta>
  /** Человеческое название носителя по префиксу: «PT1» → «Круг 20 см». */
  categoryNames?: Record<string, string>
  /** Перегенерировать файлы печати (подтверждение — на стороне окна). */
  onRegenerate?: () => void
  /** Почему кнопка неактивна (нет права и т. п.). */
  regenerateDisabledReason?: string
  regenerating?: boolean
  onOpenProduct?: (article: string) => void
}

// Строители адресов переехали в shared/media/pipelineFiles: те же ссылки нужны
// каталогу товаров. Реэкспорт сохраняет прежние импорты этого модуля.
import { maketsFileUrl, maketsPreviewUrl, productionFileUrl } from '../../../../shared/media/pipelineFiles'

export { maketsFileUrl, productionFileUrl }

export const PRINT_FILE_LIGHTBOX_WIDTH = 1200
/** Плитка списка — 48 px; просим вдвое для экранов с удвоенной плотностью. */
export const PRINT_FILE_THUMBNAIL_WIDTH = 96
/** Превью плитки крупнее: плитка — главный способ «посмотреть глазами». */
export const PRINT_FILE_TILE_WIDTH = 360

/**
 * Превью файла печати берём из хранилища макетов, а не из `production/_previews`.
 *
 * Готовое превью движок кладёт в рабочую зону тома генерации, а она с 29.08.2026
 * живёт 24 часа (`MEDIA_CACHE_TTL_HOURS`): её завели, когда кэш занял 411 ГБ и
 * держал системный раздел на 99 %. Пересоздаёт превью только шаг печати, а
 * перегенерация визуалов его намеренно не заказывает — поэтому у любого макета
 * старше суток вкладка показывала битую картинку. Хранилище же постоянно, и
 * маршрут `makets-file` строит миниатюру по запросу и кладёт её в свой кэш
 * (`getMaketsFile` → `loadOrCreateThumbnail`), то есть превью появляется в момент
 * открытия карточки.
 *
 * `previewPath` остаётся запасным путём: у промежуточных файлов маршрута в
 * хранилище места нет, и для них готовое превью — единственное, что есть.
 */
export const printFilePreviewSource = (
  file: ProductionFile,
  variant: ProductionFileVariant,
): string | null =>
  maketsPreviewUrl(file.storagePath, PRINT_FILE_LIGHTBOX_WIDTH)
  ?? (variant.previewPath ? productionFileUrl(variant.previewPath, true) : null)

export const productionFileVariantUrl = (
  file: ProductionFile,
  variant: ProductionFileVariant,
  preview = false,
): string | null => variant.path
  ? productionFileUrl(variant.path, preview)
  : file.storagePath
    ? maketsFileUrl(file.storagePath)
    : null

const FORMAT_VARIANT: Record<string, 'gray' | 'blue' | 'green' | 'red'> = {
  PDF: 'red',
  PNG: 'blue',
  JPG: 'green',
}

// ── Вид «Плитки / Список» ────────────────────────────────────────────────────

export type PrintFilesView = 'tiles' | 'list'
export const PRINT_FILES_VIEW_STORAGE_KEY = 'designCard.printFiles.view'

const readView = (): PrintFilesView => {
  try {
    return localStorage.getItem(PRINT_FILES_VIEW_STORAGE_KEY) === 'tiles' ? 'tiles' : 'list'
  } catch {
    return 'list'
  }
}

const writeView = (view: PrintFilesView): void => {
  try {
    localStorage.setItem(PRINT_FILES_VIEW_STORAGE_KEY, view)
  } catch {
    // приватный режим или квота: окно просто забудет выбор
  }
}

// ── Кнопки ───────────────────────────────────────────────────────────────────

const DownloadButton: React.FC<{ source: string | null; fileName: string; label?: string }> = ({
  source,
  fileName,
  label = 'Скачать',
}) => {
  const [busy, setBusy] = React.useState(false)
  const [failed, setFailed] = React.useState(false)

  const download = async () => {
    if (!source) return
    setBusy(true)
    setFailed(false)
    try {
      await downloadAuthorizedMedia(source, fileName)
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      disabled={busy || !source}
      onClick={() => void download()}
      title={failed ? 'Не удалось скачать файл' : undefined}
      className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
    >
      <Download className="h-3.5 w-3.5" />
      {failed ? 'Повторить' : busy ? 'Скачивание…' : label}
    </button>
  )
}

// ── Элементы списка ─────────────────────────────────────────────────────────

interface PrintItem {
  key: string
  article: string | null
  prefix: string | null
  file: ProductionFile | null
  /** Для товара без файла: почему файла нет, если шаг упал. */
  reason: string | null
}

const humanizeError = (text: string): string => parsePipelineError(text)?.plain ?? text

const thumbnailFor = (file: ProductionFile, width: number): string | null => {
  const primary = file.variants[0]
  return (
    maketsPreviewUrl(file.storagePath, width)
    ?? (primary?.previewPath ? productionFileUrl(primary.previewPath, true) : null)
  )
}

const FileIcon: React.FC<{ file: ProductionFile }> = ({ file }) => (
  <span role="img" aria-label={`${file.fileName}: превью недоступно`}>
    <FileText aria-hidden="true" className="h-4 w-4 text-gray-300 dark:text-zinc-600" />
  </span>
)

const StoragePath: React.FC<{ path: string }> = ({ path }) => (
  <span className="inline-flex min-w-0 items-center gap-0.5">
    <span className="truncate">Хранилище: {path}</span>
    <CopyButton value={path} label="путь в хранилище" size="xs" />
  </span>
)

const ArticleLabel: React.FC<{
  article: string | null
  onOpenProduct?: (article: string) => void
}> = ({ article, onOpenProduct }) => (
  <span className="inline-flex items-center gap-0.5">
    {article && onOpenProduct ? (
      <button
        type="button"
        title="Открыть товар"
        onClick={() => onOpenProduct(article)}
        className="font-mono text-[0.8125rem] font-semibold text-gray-900 hover:text-indigo-700 hover:underline dark:text-zinc-100 dark:hover:text-indigo-300"
      >
        {article}
      </button>
    ) : (
      <span className="font-mono text-[0.8125rem] font-semibold text-gray-900 dark:text-zinc-100">
        {article || '—'}
      </span>
    )}
    {article && <CopyButton value={article} label="артикул" size="xs" />}
  </span>
)

const VariantsList: React.FC<{
  file: ProductionFile
  onPreview: PrintFilesTabProps['onPreview']
}> = ({ file, onPreview }) => (
  <ul className="mt-1.5 space-y-0.5 text-2xs">
    {file.variants.map((variant) => (
      <li key={variant.purpose} className="flex flex-wrap items-center gap-x-2">
        <span className="text-gray-700 dark:text-zinc-300">{variant.purpose}</span>
        <button
          type="button"
          onClick={() => onPreview(file, variant)}
          className="text-indigo-600 hover:underline dark:text-indigo-400"
        >
          показать
        </button>
        <DownloadButton source={productionFileVariantUrl(file, variant)} fileName={file.fileName} label="скачать" />
      </li>
    ))}
  </ul>
)

const MissingMark: React.FC<{ reason: string | null; compact?: boolean }> = ({ reason, compact }) => (
  <div className={cn('flex min-w-0 flex-col', compact ? 'gap-0.5' : 'gap-1')}>
    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-zinc-400">
      <FileX className="h-3.5 w-3.5" aria-hidden="true" />
      Нет файла
    </span>
    {reason && <span className="text-2xs text-red-700 dark:text-red-400">{reason}</span>}
  </div>
)

/** Строка списка: миниатюра 48 px, артикул, формат, путь, дата, «Скачать». */
const PrintRow: React.FC<{
  item: PrintItem
  onPreview: PrintFilesTabProps['onPreview']
  onOpenProduct?: (article: string) => void
  auxiliary?: boolean
}> = ({ item, onPreview, onOpenProduct, auxiliary }) => {
  const { file } = item
  if (!file) {
    return (
      <li className="flex flex-wrap items-center gap-3 bg-gray-50/60 px-3.5 py-2.5 dark:bg-zinc-900/40">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded border border-dashed border-gray-300 text-gray-300 dark:border-zinc-700 dark:text-zinc-600">
          <FileX className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <ArticleLabel article={item.article} onOpenProduct={onOpenProduct} />
            {item.prefix && <Badge variant="gray">{item.prefix}</Badge>}
          </div>
          <MissingMark reason={item.reason} compact />
        </div>
      </li>
    )
  }

  // Ветку для превью и скачивания по умолчанию берём первую: у одноветочных
  // маршрутов она единственная, у PP2 ветки перечислены отдельными ссылками.
  const primary = file.variants[0]
  const hasBranches = file.variants.length > 1
  const primarySource = primary ? productionFileVariantUrl(file, primary, true) : null
  // Файл, которого на томе генерации нет вовсе: строку помечаем «из хранилища».
  const fromStorage = Boolean(file.storagePath && file.variants.every((variant) => !variant.path))
  // Плитка — из хранилища по той же причине, что и лайтбокс: рабочая зона с
  // готовыми превью живёт сутки, хранилище постоянно (см. printFilePreviewSource).
  const thumbnailSource = thumbnailFor(file, PRINT_FILE_THUMBNAIL_WIDTH)
  return (
    <li className="flex flex-wrap items-center gap-3 px-3.5 py-2.5">
      <button
        type="button"
        title="Посмотреть в полном размере"
        disabled={!primarySource}
        onClick={() => primarySource && primary && onPreview(file, primary)}
        className="flex h-12 w-12 shrink-0 cursor-zoom-in items-center justify-center overflow-hidden rounded border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-700"
      >
        {thumbnailSource ? (
          <SafeImg
            src={thumbnailSource}
            alt={file.fileName}
            thumbnailWidth={PRINT_FILE_THUMBNAIL_WIDTH}
            className="h-full w-full object-cover"
            iconClass="h-4 w-4"
            errorFallback={fromStorage ? <FileIcon file={file} /> : undefined}
          />
        ) : (
          <FileIcon file={file} />
        )}
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <ArticleLabel article={file.article} onOpenProduct={auxiliary ? undefined : onOpenProduct} />
          {file.categoryPrefix && <Badge variant="gray">{file.categoryPrefix}</Badge>}
          <Badge variant={FORMAT_VARIANT[file.format] || 'gray'}>{file.format || 'файл'}</Badge>
          {fromStorage && <Badge variant="indigo">из хранилища</Badge>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs text-gray-500 dark:text-zinc-400">
          <span className="font-mono">{file.fileName}</span>
          {file.storagePath ? (
            <StoragePath path={file.storagePath} />
          ) : (
            <span>{primary?.purpose || 'В печать не уходит'}</span>
          )}
          {file.createdAt && <span>{formatDateTime(file.createdAt)}</span>}
        </div>
        {hasBranches && <VariantsList file={file} onPreview={onPreview} />}
      </div>

      {!hasBranches && primary && (
        <DownloadButton source={productionFileVariantUrl(file, primary)} fileName={file.fileName} />
      )}
    </li>
  )
}

/** Плитка: крупное превью, артикул с копированием, формат, дата, «Скачать». */
const PrintTile: React.FC<{
  item: PrintItem
  onPreview: PrintFilesTabProps['onPreview']
  onOpenProduct?: (article: string) => void
}> = ({ item, onPreview, onOpenProduct }) => {
  const { file } = item
  if (!file) {
    return (
      <li className="flex flex-col gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50/60 p-2.5 dark:border-zinc-700 dark:bg-zinc-900/40">
        <div className="flex aspect-[4/3] items-center justify-center rounded-md bg-gray-100 text-gray-300 dark:bg-zinc-800 dark:text-zinc-600">
          <FileX className="h-8 w-8" aria-hidden="true" />
        </div>
        <ArticleLabel article={item.article} onOpenProduct={onOpenProduct} />
        <MissingMark reason={item.reason} />
      </li>
    )
  }
  const primary = file.variants[0]
  const hasBranches = file.variants.length > 1
  const primarySource = primary ? productionFileVariantUrl(file, primary, true) : null
  const fromStorage = Boolean(file.storagePath && file.variants.every((variant) => !variant.path))
  const thumbnailSource = thumbnailFor(file, PRINT_FILE_TILE_WIDTH)
  return (
    <li className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-2.5 dark:border-zinc-800 dark:bg-zinc-900">
      <button
        type="button"
        title="Посмотреть в полном размере"
        disabled={!primarySource}
        onClick={() => primarySource && primary && onPreview(file, primary)}
        className="flex aspect-[4/3] w-full cursor-zoom-in items-center justify-center overflow-hidden rounded-md border border-gray-100 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-800 dark:bg-zinc-950"
      >
        {thumbnailSource ? (
          <SafeImg
            src={thumbnailSource}
            alt={file.fileName}
            thumbnailWidth={PRINT_FILE_TILE_WIDTH}
            className="h-full w-full object-contain"
            iconClass="h-6 w-6"
            errorFallback={fromStorage ? <FileIcon file={file} /> : undefined}
          />
        ) : (
          <FileIcon file={file} />
        )}
      </button>
      <div className="flex flex-wrap items-center gap-1.5">
        <ArticleLabel article={file.article} onOpenProduct={onOpenProduct} />
        <Badge variant={FORMAT_VARIANT[file.format] || 'gray'}>{file.format || 'файл'}</Badge>
        {fromStorage && <Badge variant="indigo">из хранилища</Badge>}
      </div>
      <div className="min-w-0 space-y-0.5 text-2xs text-gray-500 dark:text-zinc-400">
        <div className="truncate font-mono" title={file.fileName}>{file.fileName}</div>
        {file.storagePath && <StoragePath path={file.storagePath} />}
        {file.createdAt && <div>{formatDateTime(file.createdAt)}</div>}
      </div>
      {hasBranches && <VariantsList file={file} onPreview={onPreview} />}
      {!hasBranches && primary && (
        <div className="mt-auto">
          <DownloadButton source={productionFileVariantUrl(file, primary)} fileName={file.fileName} />
        </div>
      )}
    </li>
  )
}

// ── Вкладка ─────────────────────────────────────────────────────────────────

const UNGROUPED = '__none__'

export const PrintFilesTab: React.FC<PrintFilesTabProps> = ({
  data,
  beforeAcceptance,
  onPreview,
  products = null,
  articleMeta,
  categoryNames,
  onRegenerate,
  regenerateDisabledReason,
  regenerating = false,
  onOpenProduct,
}) => {
  const [view, setView] = React.useState<PrintFilesView>(readView)
  const [bulk, setBulk] = React.useState<{ done: number; total: number; failed: number } | null>(null)

  const changeView = (next: PrintFilesView) => {
    setView(next)
    writeView(next)
  }

  if (!data) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton variant="custom" className="rounded-md" height={72} />
        <Skeleton variant="custom" className="rounded-md" height={72} />
      </div>
    )
  }

  const forPrint = data.files.filter((file) => file.storagePath)
  const auxiliary = data.files.filter((file) => !file.storagePath)
  const hasNothing = data.files.length === 0 && data.errors.length === 0 && !(products && products.length > 0)

  // Товары, у которых в печать ничего не уходит: явные строки «Нет файла».
  const withFile = new Set(forPrint.map((file) => file.article).filter((article): article is string => Boolean(article)))
  const missingArticles = (products ?? [])
    .map((product) => product.article)
    .filter((article) => !withFile.has(article))
  const total = products ? Math.max(products.length, withFile.size) : forPrint.length
  const ready = products ? products.filter((product) => withFile.has(product.article)).length : forPrint.length

  const reasonFor = (article: string, prefix: string | null): string | null => {
    const error =
      data.errors.find((item) => item.article === article)
      ?? data.errors.find((item) => !item.article && prefix && item.categoryPrefix === prefix)
    return error ? humanizeError(error.error) : null
  }

  const items: PrintItem[] = [
    ...forPrint.map((file): PrintItem => ({
      key: `file:${file.id}`,
      article: file.article,
      prefix: file.categoryPrefix ?? (file.article ? articleMeta?.[file.article]?.prefix ?? null : null),
      file,
      reason: null,
    })),
    ...missingArticles.map((article): PrintItem => {
      const prefix = articleMeta?.[article]?.prefix ?? null
      return { key: `missing:${article}`, article, prefix, file: null, reason: reasonFor(article, prefix) }
    }),
  ]

  const groups = new Map<string, PrintItem[]>()
  for (const item of items) {
    const key = item.prefix || UNGROUPED
    groups.set(key, [...(groups.get(key) ?? []), item])
  }
  const orderedGroups = [...groups.entries()].sort(([a], [b]) =>
    a === UNGROUPED ? 1 : b === UNGROUPED ? -1 : a.localeCompare(b, 'ru'),
  )
  const nameOf = (prefix: string): string | null =>
    categoryNames?.[prefix]
    ?? Object.values(articleMeta ?? {}).find((meta) => meta.prefix === prefix && meta.categoryName)?.categoryName
    ?? null

  const downloadAll = async () => {
    const queue = forPrint.filter((file) => file.storagePath)
    setBulk({ done: 0, total: queue.length, failed: 0 })
    let failed = 0
    for (const [index, file] of queue.entries()) {
      try {
        // Файл из хранилища — тот самый, что уходит в печать.
        await downloadAuthorizedMedia(maketsFileUrl(file.storagePath as string), file.fileName)
      } catch {
        failed += 1
      }
      setBulk({ done: index + 1, total: queue.length, failed })
    }
    setBulk((current) => (current ? { ...current, done: current.total } : current))
  }
  const bulkRunning = bulk !== null && bulk.done < bulk.total

  const renderGroupBody = (groupItems: PrintItem[]) =>
    view === 'tiles' ? (
      <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(13rem,1fr))] gap-3 p-0">
        {groupItems.map((item) => (
          <PrintTile key={item.key} item={item} onPreview={onPreview} onOpenProduct={onOpenProduct} />
        ))}
      </ul>
    ) : (
      <ul className="m-0 list-none divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 p-0 dark:divide-zinc-800 dark:border-zinc-800">
        {groupItems.map((item) => (
          <PrintRow key={item.key} item={item} onPreview={onPreview} onOpenProduct={onOpenProduct} />
        ))}
      </ul>
    )

  const allReady = products ? ready >= total && total > 0 : forPrint.length > 0

  return (
    <div className="space-y-4">
      {(forPrint.length > 0 || (products && products.length > 0)) && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 dark:border-zinc-800 dark:bg-zinc-900">
          <Badge variant={allReady ? 'green' : ready === 0 ? 'red' : 'yellow'}>
            {products
              ? `Готово ${ready} из ${total} ${total === 1 ? 'товара' : 'товаров'}`
              : `В печать: ${forPrint.length} ${forPrint.length === 1 ? 'товар' : 'товаров'}`}
          </Badge>
          <span className="min-w-0 flex-1 text-[0.8125rem] text-gray-600 dark:text-zinc-400">
            {products && missingArticles.length > 0
              ? `Без файла: ${missingArticles.length}. По одному файлу на товар, кнопка скачивает оригинал из хранилища.`
              : 'По одному файлу на товар, кнопка скачивает оригинал из хранилища.'}
          </span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {forPrint.length > 0 && (
              <Button
                size="sm"
                variant="secondary"
                icon={<Download className="h-4 w-4" />}
                disabled={bulkRunning}
                onClick={() => void downloadAll()}
              >
                {bulkRunning
                  ? `Скачивание ${bulk!.done + 1} из ${bulk!.total}…`
                  : `Скачать все (${forPrint.length})`}
              </Button>
            )}
            {onRegenerate && missingArticles.length > 0 && (
              <span title={regenerateDisabledReason}>
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<RefreshCw className="h-4 w-4" />}
                  loading={regenerating}
                  disabled={Boolean(regenerateDisabledReason)}
                  onClick={onRegenerate}
                >
                  Перегенерировать файлы
                </Button>
              </span>
            )}
            <span
              role="group"
              aria-label="Вид списка"
              className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 p-0.5 dark:border-zinc-700 dark:bg-zinc-800"
            >
              {([
                ['tiles', 'Плитки', LayoutGrid],
                ['list', 'Список', List],
              ] as const).map(([key, label, Icon]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={view === key}
                  onClick={() => changeView(key)}
                  className={cn(
                    'inline-flex items-center gap-1 rounded px-2 py-1 text-xs',
                    view === key
                      ? 'bg-white font-semibold text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-zinc-100'
                      : 'text-gray-500 hover:text-gray-800 dark:text-zinc-400 dark:hover:text-zinc-200',
                  )}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  {label}
                </button>
              ))}
            </span>
          </div>
          {bulk && bulk.done >= bulk.total && bulk.total > 0 && (
            <div className="basis-full text-xs text-gray-500 dark:text-zinc-400">
              {bulk.failed > 0
                ? `Скачано ${bulk.total - bulk.failed} из ${bulk.total}; не удалось: ${bulk.failed}.`
                : `Скачано файлов: ${bulk.total}.`}
            </div>
          )}
        </div>
      )}

      {data.errors.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/30">
          <div className="flex items-center gap-2 text-sm font-semibold text-red-800 dark:text-red-300">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Не создано файлов: {data.errors.length}
          </div>
          <ul className="mt-2 space-y-1.5">
            {data.errors.map((error) => (
              <li key={error.id} className="text-xs text-red-700 dark:text-red-400">
                <span className="font-mono">{error.article || error.categoryPrefix || '—'}</span>
                {' — '}
                {error.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.warnings.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <ul className="space-y-1">
            {data.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      {hasNothing && (
        <EmptyState
          title={
            beforeAcceptance ? 'Файлы появятся после приёмки' : 'Файлы для печати не создавались'
          }
          description={
            beforeAcceptance
              ? 'Производственные файлы создаёт движок сразу после того, как макет приняли.'
              : 'Движок ещё не отработал по этому макету. Отправьте его в генерацию — файлы появятся здесь.'
          }
        />
      )}

      {orderedGroups.map(([prefix, groupItems]) => {
        const done = groupItems.filter((item) => item.file).length
        const name = prefix === UNGROUPED ? null : nameOf(prefix)
        return (
          <section key={prefix} aria-label={prefix === UNGROUPED ? 'Без категории' : `Категория ${prefix}`}>
            <h3 className="mb-1.5 flex flex-wrap items-center gap-x-2 text-xs font-semibold text-gray-700 dark:text-zinc-300">
              {prefix === UNGROUPED ? (
                <span>Без категории</span>
              ) : (
                <>
                  <span className="font-mono">{prefix}</span>
                  {name && <span className="font-normal text-gray-600 dark:text-zinc-400">{name}</span>}
                </>
              )}
              <span
                className={cn(
                  'rounded-full px-1.5 py-px text-2xs font-medium tabular-nums',
                  done === groupItems.length
                    ? 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
                )}
              >
                {done} из {groupItems.length}
              </span>
            </h3>
            {renderGroupBody(groupItems)}
          </section>
        )
      })}

      {auxiliary.length > 0 && (
        <section>
          <h3 className="mb-1.5 text-xs font-semibold text-gray-700 dark:text-zinc-300">
            Промежуточные файлы маршрута
          </h3>
          <p className="mb-2 text-2xs text-gray-500 dark:text-zinc-400">
            Движок создал их по пути к печатному файлу. В хранилище макетов не копируются.
          </p>
          <ul className="m-0 list-none divide-y divide-gray-100 overflow-hidden rounded-lg border border-dashed border-gray-300 p-0 dark:divide-zinc-800 dark:border-zinc-700">
            {auxiliary.map((file) => (
              <PrintRow
                key={file.id}
                item={{ key: file.id, article: file.article, prefix: file.categoryPrefix, file, reason: null }}
                onPreview={onPreview}
                auxiliary
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

export default PrintFilesTab
